// Admin → Survey Responses tab (Phase 2 of Follow-Up Hub).
// Shows post-lab survey submissions in a sortable table with status filter chips,
// detail dialog (full response + status select + admin notes), and CSV export.
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Loader2, RefreshCw, Search, Download, Star, Mail, Save, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { adminFetch, adminPatch } from "@/lib/api";

const STATUS_STYLES = {
  "New": "border-[var(--line)] text-ink/65 bg-white",
  "Needs Follow-Up": "border-amber-300 text-amber-800 bg-amber-50",
  "Sent Resources": "border-sky-300 text-sky-800 bg-sky-50",
  "Interested in Clinic": "border-violet-300 text-violet-800 bg-violet-50",
  "Interested in 1:1": "border-emerald-300 text-emerald-800 bg-emerald-50",
  "Interested in Done-For-You": "border-fuchsia-300 text-fuchsia-800 bg-fuchsia-50",
  "Testimonial Opportunity": "border-brand/40 text-brand bg-brand-soft",
  "Completed": "border-emerald-400 text-emerald-700 bg-emerald-50",
  "Closed": "border-[var(--line)] text-ink/55 bg-bone",
};

const NEXT_BUILD_LABELS = {
  ai_assisted_website: "AI-Assisted Website",
  landing_page: "Landing Page",
  lead_funnel: "Lead Funnel",
  simple_app_prototype: "Simple App Prototype",
  customer_intake_system: "Customer Intake System",
  dashboard: "Dashboard",
  not_sure: "Not sure yet",
};

const SUPPORT_LABELS = {
  build_on_own: "Build on my own",
  group_troubleshooting: "Group troubleshooting",
  review_process_or_prompt: "Review my process map / prompt",
  private_1on1: "Private 1:1 Build-With-You",
  built_for_me: "Built for me",
  need_guidance: "Need guidance",
};

const TESTIMONIAL_LABELS = {
  written: "Written",
  video: "Video",
  maybe_later: "Maybe later",
  no: "No",
};

export default function SurveyResponses({ pw }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [], counts: {}, total: 0, statuses: [] });
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState({ status: "", admin_notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== "All") params.set("status", status);
      if (debouncedSearch) params.set("search", debouncedSearch);
      const path = `/admin/lab-followup/surveys${params.toString() ? "?" + params.toString() : ""}`;
      const d = await adminFetch(path, pw);
      setData(d);
    } catch {
      toast.error("Failed to load surveys");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, debouncedSearch]);

  const openDetail = (row) => {
    setSelected(row);
    setEditing({ status: row.status || "New", admin_notes: row.admin_notes || "" });
  };

  const saveDetail = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await adminPatch(`/admin/lab-followup/surveys/${selected.id}`, pw, {
        status: editing.status,
        admin_notes: editing.admin_notes,
      });
      toast.success("Saved.");
      // Update the selected card + reload the list.
      setSelected(res.survey);
      await load();
    } catch (e) {
      toast.error(e?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = async () => {
    try {
      const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await fetch(`${apiBase}/admin/lab-followup/surveys.csv`, {
        headers: { "X-Admin-Password": pw },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-build-lab-surveys-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    }
  };

  const filterChips = useMemo(() => {
    const chips = [["All", data.total]];
    (data.statuses || []).forEach((s) => chips.push([s, data.counts[s] || 0]));
    return chips;
  }, [data]);

  return (
    <div className="space-y-5" data-testid="survey-responses-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-1">Post-Training</div>
          <h2 className="font-display text-2xl tracking-[-0.025em]">AI Build Lab Survey Responses</h2>
          <p className="text-sm text-ink/60 mt-1">
            Submissions auto-segment by support need and rating. Click any row for the full response, change the status, or add internal notes.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={load} data-testid="surveys-refresh" className="btn-outline-ink rounded-full h-10 px-4"><RefreshCw size={13} className="mr-1.5" />Refresh</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="surveys-export-csv" className="btn-outline-ink rounded-full h-10 px-4"><Download size={13} className="mr-1.5" />Export CSV</Button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <Input
            placeholder="Search name, email, business, or build idea..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="surveys-search"
            className="pl-9 bg-bone border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0"
          />
        </div>
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="bg-bone border border-[var(--line)] flex-wrap h-auto">
          {filterChips.map(([label, count]) => (
            <TabsTrigger key={label} value={label} data-testid={`surveys-filter-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {label} ({count})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="rounded-2xl border border-[var(--line)] overflow-hidden bg-bone overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--line)] hover:bg-transparent">
              <TableHead className="text-ink/60">Participant</TableHead>
              <TableHead className="text-ink/60">Rating</TableHead>
              <TableHead className="text-ink/60">Support need</TableHead>
              <TableHead className="text-ink/60">Status</TableHead>
              <TableHead className="text-ink/60">Follow-up</TableHead>
              <TableHead className="text-ink/60">Submitted</TableHead>
              <TableHead className="text-ink/60 text-right">View</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow className="border-[var(--line)] hover:bg-transparent"><TableCell colSpan={7} className="text-center text-ink/50 py-10"><Loader2 size={14} className="inline mr-2 animate-spin" /> Loading…</TableCell></TableRow>
            )}
            {!loading && data.rows.length === 0 && (
              <TableRow className="border-[var(--line)] hover:bg-transparent"><TableCell colSpan={7} className="text-center text-ink/50 py-12">No survey responses match this filter yet.</TableCell></TableRow>
            )}
            {!loading && data.rows.map((row) => (
              <TableRow
                key={row.id}
                className="border-[var(--line)] hover:bg-white cursor-pointer"
                data-testid={`survey-row-${row.id}`}
                onClick={() => openDetail(row)}
              >
                <TableCell className="font-medium text-ink">
                  <div>{row.participant_name}</div>
                  <div className="text-xs text-ink/55">{row.email}{row.business_name ? ` · ${row.business_name}` : ""}</div>
                </TableCell>
                <TableCell><RatingStars n={row.star_rating || 0} /></TableCell>
                <TableCell className="text-ink/75 text-xs">{SUPPORT_LABELS[row.support_need] || row.support_need}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={STATUS_STYLES[row.status] || STATUS_STYLES["New"]}>{row.status}</Badge>
                </TableCell>
                <TableCell>
                  {row.needs_follow_up_flag ? (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-700"><AlertCircle size={11} />Flagged</span>
                  ) : <span className="text-xs text-ink/35">—</span>}
                </TableCell>
                <TableCell className="text-xs text-ink/65 whitespace-nowrap">{row.submitted_at ? new Date(row.submitted_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" className="btn-outline-ink rounded-full h-8 px-3 text-xs" onClick={(e) => { e.stopPropagation(); openDetail(row); }}>View</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white border border-[var(--line)] text-ink max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="survey-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl tracking-[-0.025em] flex items-center gap-2">
                  {selected.participant_name}
                  <Badge variant="outline" className={STATUS_STYLES[selected.status] || STATUS_STYLES["New"]}>{selected.status}</Badge>
                </DialogTitle>
                <DialogDescription className="text-ink/65">
                  Submitted {selected.submitted_at ? new Date(selected.submitted_at).toLocaleString() : "—"}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 pt-2">
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <Detail label="Email" value={selected.email} icon={<Mail size={11} />} link={`mailto:${selected.email}`} />
                  <Detail label="Business" value={selected.business_name || "—"} />
                  <Detail label="Rating" value={<RatingStars n={selected.star_rating || 0} showNumber />} />
                  <Detail label="Next build" value={NEXT_BUILD_LABELS[selected.next_build_interest] || "—"} />
                  <Detail label="Process mapping" value={(selected.mapping_understanding || "").toUpperCase()} />
                  <Detail label="Emergent signup" value={(selected.emergent_signup_status || "").replace(/_/g, " ").toUpperCase()} />
                  <Detail label="Support need" value={SUPPORT_LABELS[selected.support_need] || selected.support_need} />
                  <Detail label="Wants follow-up" value={(selected.wants_follow_up || "").toUpperCase()} />
                  <Detail label="Testimonial" value={TESTIMONIAL_LABELS[selected.testimonial_choice] || selected.testimonial_choice} />
                  <Detail label="Permission" value={selected.permission_granted ? "✓ Granted" : "✗ Not granted"} />
                  {selected.build_link && <Detail label="Build link" value={selected.build_link} link={selected.build_link} />}
                </div>

                <LongField label="What they came to build" value={selected.attended_to_build} />
                <LongField label="Most valuable part" value={selected.most_valuable_part} />
                {selected.unclear_or_overwhelming && <LongField label="What still feels unclear" value={selected.unclear_or_overwhelming} />}
                {selected.future_build_interest && <LongField label="What they'd love to build next" value={selected.future_build_interest} />}

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <div className="eyebrow !mb-1.5">Update status</div>
                    <Select value={editing.status} onValueChange={(v) => setEditing((s) => ({ ...s, status: v }))}>
                      <SelectTrigger data-testid="survey-status-select" className="bg-bone border border-[var(--line)]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(data.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-ink/55 mt-1.5">Setting <em>Completed</em> or <em>Closed</em> visually retires the row but does not delete it.</p>
                  </div>
                  <div>
                    <div className="eyebrow !mb-1.5">Auto-flag</div>
                    <div className="rounded-xl border border-[var(--line)] bg-bone px-3 py-2 text-sm">
                      {selected.needs_follow_up_flag
                        ? <span className="text-amber-700 inline-flex items-center gap-1.5"><AlertCircle size={12} /> Marked for follow-up</span>
                        : <span className="text-ink/55">Not flagged</span>}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="eyebrow !mb-1.5">Admin notes (internal)</div>
                  <Textarea
                    rows={5}
                    value={editing.admin_notes}
                    onChange={(e) => setEditing((s) => ({ ...s, admin_notes: e.target.value }))}
                    placeholder="Conversation history, follow-up dates, things to remember…"
                    data-testid="survey-admin-notes"
                    className="bg-bone border border-[var(--line)] focus-visible:ring-brand focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              <DialogFooter className="pt-4 gap-2">
                <Button variant="outline" onClick={() => setSelected(null)} className="btn-outline-ink rounded-full h-10 px-5">Close</Button>
                <Button onClick={saveDetail} disabled={saving} data-testid="survey-save" className="btn-ink rounded-full h-10 px-5">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <><Save size={13} className="mr-1.5" /> Save</>}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RatingStars({ n, showNumber }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={14} className={i <= n ? "fill-brand text-brand" : "text-ink/15"} />
      ))}
      {showNumber && <span className="ml-2 text-xs text-ink/65 font-mono-spec">{n}/5</span>}
    </div>
  );
}

function Detail({ label, value, icon, link }) {
  return (
    <div>
      <div className="eyebrow !mb-0.5">{label}</div>
      <div className="flex items-center gap-1.5 text-sm text-ink">
        {icon && <span className="text-ink/40">{icon}</span>}
        {link ? <a href={link} target="_blank" rel="noopener noreferrer" className="text-brand underline hover:no-underline truncate max-w-[260px]">{value}</a> : <span>{value}</span>}
      </div>
    </div>
  );
}

function LongField({ label, value }) {
  return (
    <div>
      <div className="eyebrow !mb-1.5">{label}</div>
      <div className="rounded-xl border border-[var(--line)] bg-bone p-3 text-sm text-ink/85 whitespace-pre-wrap leading-relaxed">{value}</div>
    </div>
  );
}
